import React from 'react';
import compose from 'lodash/fp/compose';
import { withRouter } from 'react-router';
import { withConfig } from '@catalogo/core-connect/config';
import { withCookies } from '@catalogo/core-connect/cookies';
import { withDevice, WithDeviceProps as IWithDeviceInjectedProps } from '@catalogo/core-connect/device';
import { withProductCache } from '@catalogo/service-americanas-product/src/hocs/product-cache';
import { getWarrantyUrl } from '@catalogo/core-helpers/url';
import { withProductQuantity } from '@catalogo/service-americanas-product/src/hocs/product-quantity';
import { withAddToCart } from '@catalogo/service-americanas-cart/src/hocs/add-to-cart';
import { IWithAddToCartInjectedProps } from '@catalogo/service-americanas-cart/src/types/add-to-cart';
import { withModalWarrantyExperiment } from '@catalogo/service-americanas-product/src/experiments/modal-warranty-experiment';
import { IInjectedProps as IModalWarrantyExperimentProps } from '@catalogo/service-americanas-product/src/experiments/modal-warranty-experiment/types';
import { MODAL_WARRANTY_URL_REF } from '@catalogo/service-americanas-cart/src/helpers/add-to-cart-pdp';
import { useAddToCartPDP } from '@catalogo/service-americanas-cart/src/hooks/add-to-cart-pdp';
import { handleAddToCartInModal } from '@catalogo/service-americanas-cart/src/helpers/add-to-cart-pdp';
import { useStore } from '@catalogo/service-americanas-product/src/hooks/store';
import { WithSelectStore } from '../types';

export const withSelectStore = (Component: React.FC) => (
  props: WithSelectStore & IWithAddToCartInjectedProps & IWithDeviceInjectedProps & IModalWarrantyExperimentProps
) => {
  const {
    url,
    parsedQs,
    routeId,
    cookies,
    match,
    history,
    productQty,
    config,
    cartId,
    cartLines,
    addToCart,
    addToCartLoading,
    addToCartError,
    isAddedToCart,
    isUpdatedToCart,
    modalWarrantyExperiment,
    device,
  } = props;

  const productId = match?.params?.id;
  const { data, error, loading, product, selectedStore } = useStore({ cookies, productId, routeId, parsedQs });
  const sellerStoreId = selectedStore?.sellerStoreId;
  const currentSku = { offers: { result: [product?.lasaOffer] }, productId };
  const historyStateText = history?.location?.state?.text;
  const hasServices = product?.offer?.productServices?.length > 0;
  const brand = config?.brandNameLower;
  const showWarrantyModal =
    hasServices &&
    brand?.toLowerCase() === 'americanas' &&
    (device?.toLowerCase() === 'desktop' || modalWarrantyExperiment?.toUpperCase() === 'NEW');

  let warrantyUrl = getWarrantyUrl(productId, currentSku, sellerStoreId, productQty);
  if (showWarrantyModal) {
    warrantyUrl = MODAL_WARRANTY_URL_REF;
  }

  const { handleAddProduct } = useAddToCartPDP({
    showModal: false,
    url: warrantyUrl,
    history,
    brand,
    addToCart,
    cartId,
    cartLines,
    isAddedToCart,
    isUpdatedToCart,
    addToCartLoading,
    addToCartError,
    offers: [{ offerId: currentSku?.offers?.result?.[0]?.id, quantity: productQty }],
    hasServices,
    currentSellerStoreId: sellerStoreId,
  });

  const withSelectStoreProps = {
    product,
    currentSku,
    sellerStoreId,
    data,
    error,
    loading,
    hasReceiveDescription: historyStateText?.includes('receba') ?? true,
    handleAddProduct: handleAddToCartInModal({ history, url, warrantyUrl, handleAddProduct }),
  };

  return <Component {...props} {...withSelectStoreProps} />;
};

export const withSelectStoreToModal = compose(
  withConfig,
  withCookies,
  withDevice,
  withProductCache,
  withProductQuantity,
  withAddToCart,
  withModalWarrantyExperiment,
  withSelectStore
);

export default compose(withRouter, withProductQuantity, withSelectStore);
